/**
 * Bokio OAuth 2.0 client — shared by the web app's OAuth routes and the
 * runtime's token refresh.
 *
 * One Squidler-owned OAuth app per environment. Bokio apps are registered in
 * either "test" or "prod" mode, so dev and prod hold different credentials:
 *   BOKIO_OAUTH_CLIENT_ID
 *   BOKIO_OAUTH_CLIENT_SECRET
 *   BOKIO_OAUTH_REDIRECT_URI
 *
 * Shaped after `../fortnox/oauth.ts` deliberately — same env triple, same Basic
 * auth, same form-encoded token call — but three things genuinely differ:
 *
 *  1. SCOPES CARRY THE READ/WRITE DECISION. Bokio has `invoices:read` and
 *     `invoices:write` as separate scopes, so a read-only connection is one we
 *     never asked write for and Bokio refuses the write itself. Fortnox has no
 *     read-only scopes at all, which is why its gate is the whole boundary.
 *     The consequence here: widening an area needs this flow run again.
 *
 *  2. THE TOKEN RESPONSE NAMES THE TENANT. `tenant_id` is the Bokio company id
 *     — the `{companyId}` path segment and our natural key — so unlike Fortnox
 *     we learn which company was authorized without a follow-up call.
 *
 *  3. RE-AUTH CAN SKIP THE COMPANY PICKER. `bokio_tenantid` pre-selects the
 *     company on the consent screen. Fortnox has no such parameter, which is
 *     why re-authorizing a connected Fortnox company makes the user choose it
 *     from a list again — a real complaint from a live customer. Always pass it
 *     when re-authorizing a connection we already hold.
 *
 * THE REFRESH TOKEN ROTATES, AND EXPIRES AFTER 30 IDLE DAYS. Both are stated
 * in Bokio's prose documentation and in neither the OpenAPI spec nor the token
 * response:
 *
 *   "Refresh token expires when used or if unused for more than 30 days. When
 *    using the refresh token a new refresh token is returned."
 *   — docs.bokio.se/reference/authentication-for-public-integrations-oauth
 *
 * Rotation makes the per-connection advisory lock in `tools/bokio/tokens.ts`
 * load-bearing rather than prudent: two concurrent refreshes spend the same
 * token twice and destroy the connection.
 *
 * The 30-day idle expiry is why `tools/bokio/refreshable.ts` registers Bokio
 * with the sweeper. Refresh-on-use alone would lose exactly the connections
 * nobody touches — a bureau's quiet client ledger — and surface it at the worst
 * moment. This integration was very nearly built without one, because the
 * machine-readable spec says nothing about lifetimes.
 */

const BASE_URL = "https://api.bokio.se/v1";
const AUTH_URL = `${BASE_URL}/authorize`;
const TOKEN_URL = `${BASE_URL}/token`;

interface OAuthConfig {
  readonly clientId: string;
  readonly clientSecret: string;
  readonly redirectUri: string;
}

/** Read the env triple, or null when it is not fully set. Both the getter and
 *  the configured-check go through here so the var names appear once. */
const readConfig = (): OAuthConfig | null => {
  const clientId = process.env.BOKIO_OAUTH_CLIENT_ID;
  const clientSecret = process.env.BOKIO_OAUTH_CLIENT_SECRET;
  const redirectUri = process.env.BOKIO_OAUTH_REDIRECT_URI;
  if (!clientId || !clientSecret || !redirectUri) return null;
  return { clientId, clientSecret, redirectUri };
};

const getConfig = (): OAuthConfig => {
  const config = readConfig();
  if (!config) {
    throw new Error(
      "Bokio OAuth not configured — set BOKIO_OAUTH_CLIENT_ID, " +
      "BOKIO_OAUTH_CLIENT_SECRET, and BOKIO_OAUTH_REDIRECT_URI",
    );
  }
  return config;
};

export const isBokioConfigured = (): boolean => readConfig() !== null;

// --- Auth URL ---

interface BuildAuthUrlArgs {
  readonly scopes: readonly string[];
  readonly state: string;
  /**
   * The Bokio company to pre-select, when re-authorizing one we already hold.
   * Omit for a first connection — the user picks the company then.
   */
  readonly tenantId?: string;
}

/**
 * Bokio rejects a shorter `state` with `invalid_request: state parameter too
 * short`. Measured against the live endpoint, not documented anywhere: 7
 * characters is refused and 8 is accepted.
 *
 * Our nonce is `randomBytes(32).toString("base64url")` — 43 characters — so
 * this never fires today. It exists because the failure it prevents is a 400
 * from Bokio at the point a user clicks Connect, blamed on the credentials
 * rather than on a nonce someone shortened.
 */
export const MIN_STATE_LENGTH = 8;

/**
 * Build the consent URL.
 *
 * Bokio's spec marks `state` required rather than optional, which matches what
 * we do anyway: it is persisted server-side in `oauth_states`, bound to the
 * initiating user and instance, and consumed single-use on callback.
 */
export const buildAuthUrl = ({ scopes, state, tenantId }: BuildAuthUrlArgs): string => {
  if (state.length < MIN_STATE_LENGTH) {
    throw new Error(
      `Bokio requires a state of at least ${MIN_STATE_LENGTH} characters; got ${state.length}`,
    );
  }
  const { clientId, redirectUri } = getConfig();
  const params = new URLSearchParams({
    response_type: "code",
    client_id: clientId,
    redirect_uri: redirectUri,
    scope: scopes.join(" "),
    state,
  });
  if (tenantId) {
    params.set("bokio_tenantid", tenantId);
    // The only tenant type Bokio currently supports; sent explicitly so a
    // future second type cannot silently change what we are asking for.
    params.set("bokio_tenanttype", "company");
  }
  return `${AUTH_URL}?${params.toString()}`;
};

// --- Token exchange ---

export interface BokioTokenResponse {
  readonly access_token: string;
  readonly refresh_token: string;
  readonly expires_in: number;
  readonly token_type: string;
  /** The Bokio company id — the `{companyId}` path segment, and our key. */
  readonly tenant_id: string;
  readonly tenant_type: string;
  readonly connection_id: string;
}

export class BokioOAuthError extends Error {
  constructor(public readonly code: string, message: string) {
    super(message);
    this.name = "BokioOAuthError";
  }
}

/** A spent or revoked grant: no retry helps, the browser flow must run again. */
export const isUnrecoverable = (err: unknown): boolean =>
  err instanceof BokioOAuthError
  && (err.code === "invalid_grant" || err.code === "invalid_client");

const basicAuth = (clientId: string, clientSecret: string): string =>
  `Basic ${Buffer.from(`${clientId}:${clientSecret}`).toString("base64")}`;

const postToken = async (body: URLSearchParams): Promise<BokioTokenResponse> => {
  const { clientId, clientSecret } = getConfig();
  const res = await fetch(TOKEN_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json",
      Authorization: basicAuth(clientId, clientSecret),
    },
    body,
  });
  const parsed: unknown = await res.json().catch(() => ({}));
  const payload = (parsed ?? {}) as Record<string, unknown>;
  if (!res.ok) {
    const code = typeof payload.error === "string" ? payload.error : `http_${res.status}`;
    const description = typeof payload.error_description === "string"
      ? payload.error_description
      : `HTTP ${res.status}`;
    throw new BokioOAuthError(code, description);
  }
  if (typeof payload.access_token !== "string" || typeof payload.refresh_token !== "string") {
    // A 200 without both tokens must never be written over the only live
    // credential we hold — the same trap as Fortnox, where doing so destroys
    // the connection permanently.
    throw new BokioOAuthError(
      "malformed_token_response",
      "token response missing access_token or refresh_token",
    );
  }
  return payload as unknown as BokioTokenResponse;
};

/**
 * An APP-level token, via the client-credentials grant.
 *
 * Not tied to any company: it reaches the General API only (`/connections`),
 * which is how we learn a tenant's NAME without holding a company-scoped token.
 *
 * Deliberately not routed through `postToken`, which insists on a
 * `refresh_token` — correct for the flows that have one, and wrong here.
 * Bokio's own spec says so ("Returned for authorization_code and
 * refresh_token grant types"), and the live endpoint returns null, so reusing
 * that helper would reject a perfectly good token.
 */
export const fetchAppAccessToken = async (): Promise<string> => {
  const { clientId, clientSecret } = getConfig();
  const res = await fetch(TOKEN_URL, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
      Accept: "application/json",
      Authorization: basicAuth(clientId, clientSecret),
    },
    body: new URLSearchParams({ grant_type: "client_credentials" }),
  });
  const payload = (await res.json().catch(() => ({}))) as Record<string, unknown>;
  if (!res.ok || typeof payload.access_token !== "string") {
    const description = typeof payload.error_description === "string"
      ? payload.error_description
      : `HTTP ${res.status}`;
    throw new BokioOAuthError(
      typeof payload.error === "string" ? payload.error : `http_${res.status}`,
      description,
    );
  }
  return payload.access_token;
};

/** Exchange an authorization code for tokens. */
export const exchangeCode = (code: string): Promise<BokioTokenResponse> => {
  const { redirectUri } = getConfig();
  return postToken(new URLSearchParams({
    grant_type: "authorization_code",
    code,
    // Sent again at this step and compared byte for byte against the authorize
    // request. A trailing slash difference fails here, not there.
    redirect_uri: redirectUri,
  }));
};

/**
 * Redeem a refresh token.
 *
 * Persist the returned `refresh_token` before the next refresh. Bokio does not
 * document whether it rotates; treating it as if it does costs one column
 * write and is the only safe reading, because the alternative failure is a
 * connection that dies unpredictably.
 */
export const refreshAccessToken = (refreshToken: string): Promise<BokioTokenResponse> =>
  postToken(new URLSearchParams({
    grant_type: "refresh_token",
    refresh_token: refreshToken,
  }));
